import {
    useState,
    useMemo,
    useCallback,
    useRef,
    useEffect,
    ChangeEventHandler,
    MouseEventHandler,
    KeyboardEventHandler,
} from 'react';
import { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import { objectTypes, objectType } from '@api/Devices.api';
import { NO_GROUP_ID } from '@api/Groups.api';
import { ICloseEvent } from '@core/interfaces/ICloseEvent';
import { BindedMomentChangeHandler } from '@core/interfaces/moment-change-handler';
import { useStore } from '@ui/hooks/useStore';
import useKeyDown from '@ui/hooks/useKeyDown';
import { getDeviceObjectKeys } from '@utils/getDeviceObjectKeys';
import getBtnElementId from '@utils/getBtnElementId';
import { IState } from './IState';

const useEditDevice = (onClose: ICloseEvent['onClose']) => {
    const { devices, user, map, groups, lang, app } = useStore();
    const key = useKeyDown();
    const imei = devices.editedDeviceImei ?? '';
    const [currentDevice] = devices.deviceList.filter((d) => d.imei === imei);
    const deviceName = currentDevice?.name;
    const deviceIconNum = currentDevice?.icon;
    const groupId = currentDevice?.groupId;
    const type = currentDevice?.type;
    const additionalInfo = currentDevice?.additionalInfo;

    const initState: IState = {
        groupId,
        type,
        additionalInfo,
        deviceName,
        deviceIconNum,
        keys: getDeviceObjectKeys(type),
        deviceNameError: false,
        iconSelectVisible: false,
        iconSelectEl: null,
        savedAlertVisible: false,
    };
    const [state, setState] = useState(initState);

    const types = useMemo(() => Object.values(objectTypes), []);
    const values = {
        types,
        imei,
        deviceName,
    };

    const iconSelectEl = useRef<HTMLButtonElement | null>(null);
    const refs = {
        iconSelectEl,
    };

    const handleInputClick = useCallback(() => {
        devices.resetServerError();
        app.resetDemoModeError();
        devices.resetDeviceAlreadyExistError();
        devices.resetImeiNotExistError();
        devices.resetNoRightsError();
        setState((prev) => ({
            ...prev,
            deviceNameError: false,
        }));
    }, [setState]);

    const deviceNameValidate = useCallback(
        (name: string) => {
            if (!name) {
                setState((prev) => ({
                    ...prev,
                    deviceNameError: true,
                }));
                return false;
            } else {
                setState((prev) => ({
                    ...prev,
                    deviceNameError: false,
                }));
                return true;
            }
        },
        [setState],
    );

    const handleGroupChange: ChangeEventHandler<HTMLInputElement> = useCallback(
        (e) => {
            handleInputClick();
            const groupId = e.target.value;
            setState((prev) => ({
                ...prev,
                groupId,
            }));
        },
        [setState, handleInputClick],
    );

    const handleTypeChange: SelectProps<objectType>['onChange'] = useCallback(
        (e: SelectChangeEvent<objectType>) => {
            handleInputClick();
            const type = e.target.value as objectType;
            setState((prev) => ({
                ...prev,
                type,
                keys: getDeviceObjectKeys(type),
            }));
        },
        [handleInputClick, setState],
    );

    const handleAdditionalInfoChange: ChangeEventHandler<HTMLInputElement> =
        useCallback(
            (e) => {
                handleInputClick();
                const target = e.target;
                const key = target.id;
                const value = target.value;
                setState((prev) => ({
                    ...prev,
                    additionalInfo: {
                        ...prev.additionalInfo,
                        [key]: value,
                    },
                }));
            },
            [handleInputClick, setState],
        );

    const handleAdditionalInfoDateChange: BindedMomentChangeHandler =
        useCallback(
            (id, e) => {
                handleInputClick();
                const value = e ? e.format('DD.MM.YYYY') : '';
                setState((prev) => ({
                    ...prev,
                    additionalInfo: {
                        ...prev.additionalInfo,
                        [id]: value,
                    },
                }));
            },
            [handleInputClick, setState],
        );

    const handleDeviceNameChange: ChangeEventHandler<HTMLInputElement> =
        useCallback(
            (e) => {
                handleInputClick();
                const deviceName = e.target.value;
                setState((prev) => ({
                    ...prev,
                    deviceName,
                }));
            },
            [handleInputClick, setState],
        );

    const handleSaveClick = useCallback(() => {
        handleInputClick();
        const {
            deviceName: name,
            deviceIconNum: icon,
            groupId,
            type,
            additionalInfo: info,
        } = state;
        const isDeviceNameValid = deviceNameValidate(name);
        const items = getDeviceObjectKeys(type);
        const additionalInfo = items.reduce((acc, item) => {
            const key = item.name;
            const value = info[key as keyof typeof info] ?? '';
            return { ...acc, [key]: value };
        }, {});
        if (isDeviceNameValid && !app.demoMode) {
            devices.changeDeviceDescription({
                name,
                icon,
                type,
                additionalInfo,
            });
            groups.addDevice(groupId);
        } else if (app.demoMode) app.setDemoModeError();
    }, [handleInputClick, state, deviceNameValidate]);

    const handleSelectIconBtnClick: MouseEventHandler<HTMLButtonElement> =
        useCallback(
            (e) => {
                handleInputClick();
                e.stopPropagation();
                setState((prev) => ({
                    ...prev,
                    iconSelectEl: iconSelectEl.current,
                    iconSelectVisible: !prev.iconSelectVisible,
                }));
            },
            [handleInputClick, setState],
        );

    const handleSelectIconWindowClose = useCallback(() => {
        handleInputClick();
        setState((prev) => ({ ...prev, iconSelectVisible: false }));
    }, [handleInputClick, setState]);

    const handleIconClick: MouseEventHandler<HTMLButtonElement> = useCallback(
        (e) => {
            handleInputClick();
            const id = getBtnElementId(e.target as HTMLButtonElement);
            if (!id) return;
            const deviceIconNum = parseInt(id, 10);
            setState((prev) => ({
                ...prev,
                deviceIconNum,
                iconSelectVisible: false,
            }));
        },
        [handleInputClick, setState],
    );

    const handleKey = useCallback(
        (key: string) => {
            switch (key) {
                case 'Escape':
                    onClose();
                    break;
                case 'Enter':
                    handleSaveClick();
                    break;
            }
        },
        [handleSaveClick],
    );

    const handleKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
        (e) => {
            const { key } = e;
            if (key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                handleKey(key);
            }
        },
        [handleKey],
    );

    const handleBtnKeyDown: KeyboardEventHandler<HTMLButtonElement> =
        useCallback((e) => {
            const { key } = e;
            if (key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                const target = e.target as HTMLButtonElement;
                target.click();
            }
        }, []);

    const handleSaveAlertClose = useCallback(() => {
        setState((prev) => ({ ...prev, savedAlertVisible: false }));
    }, [setState]);

    const handlers = {
        handleDeviceNameChange,
        handleSaveClick,
        handleInputClick,
        handleSelectIconBtnClick,
        handleSelectIconWindowClose,
        handleIconClick,
        handleKeyDown,
        handleBtnKeyDown,
        handleGroupChange,
        handleTypeChange,
        handleAdditionalInfoChange,
        handleAdditionalInfoDateChange,
        handleSaveAlertClose,
    };

    useEffect(() => {
        handleInputClick();
        const element = document.activeElement as HTMLElement;
        if (element) {
            element.blur();
        }
        return () => {
            handleInputClick();
            app.closeDeviceEdit();
        };
    }, []);
    useEffect(() => {
        setState((prev) => ({
            ...prev,
            ...initState,
        }));
    }, [imei]);
    useEffect(() => {
        if (
            (devices.operationSuccess && !groups.addDeviceLoading) ||
            (groups.operationSuccess && !devices.changeDeviceDescriptionLoading)
        ) {
            devices.resetOperationSuccess();
            groups.resetOperationSuccess();
            map.setReload();
            setState((prev) => ({ ...prev, savedAlertVisible: true }));
        }
    }, [
        devices.operationSuccess,
        groups.operationSuccess,
        groups.addDeviceLoading,
        devices.changeDeviceDescriptionLoading,
    ]);
    useEffect(() => {
        handleKey(key);
    }, [key]);
    useEffect(() => {
        const idList = groups.groupsList.map(({ id }) => id);
        if (!groups.loading && idList.indexOf(state.groupId) === -1) {
            if (idList.indexOf(groupId) !== -1) {
                setState((prev) => ({ ...prev, groupId }));
            } else {
                setState((prev) => ({
                    ...prev,
                    groupId: NO_GROUP_ID,
                }));
            }
        }
    }, [groups.groupsList, groups.loading]);
    useEffect(() => {
        if (!currentDevice) onClose();
    }, [currentDevice]);

    return {
        devices,
        groups,
        app,
        lang,
        state,
        handlers,
        refs,
        values,
    };
};

export default useEditDevice;
